import React, { useState } from "react";
import Nav from "../master/NavBar";
import Header from "../master/SideBar";
import { BankAdd } from "../../api/BankService";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import BankLayout from "./BankLayout";

const BanksAdd = () => {
  const [loading, setLoading] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [upiId, setUpiId] = useState("");

  const BankCreate = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const qrFile = formData.get("qr_img");

    if (qrFile?.size > 0 && !formData.get("upi_id")?.trim()) {
      SweetAlertShow(
        "warning",
        "Validation",
        "UPI ID is required when QR Image is uploaded.",
      );
      return;
    }

    setLoading(true);

    try {
      const response = await BankAdd(formData);

      if (response?.data?.status) {
        e.target.reset();
        setQrImage(null);
        setUpiId("");

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid py-3">
              <div className="row">
                <BankLayout />

                <div className="col-12">
                  {/* =====================================================
                      PAGE HEADER
                  ===================================================== */}

                  <div className="bank-page-header mb-3">
                    <div className="bank-header-left">
                      <div className="bank-header-icon">
                        <i className="ri-bank-line"></i>
                      </div>

                      <div>
                        <h4 className="mb-1 fw-semibold">Add Bank Account</h4>

                        <p className="text-muted mb-0">
                          Add bank details and configure UPI payment
                          information.
                        </p>
                      </div>
                    </div>

                    <div className="bank-header-badge">
                      <i className="ri-shield-check-line me-1"></i>
                      Secure Account Setup
                    </div>
                  </div>

                  {/* =====================================================
                      FORM CARD
                  ===================================================== */}

                  <div className="card border-0 shadow-sm">
                    <form onSubmit={BankCreate}>
                      <div className="card-body p-4">
                        {/* =================================================
                            BANK DETAILS
                        ================================================= */}

                        <div className="form-section">
                          <div className="form-section-header">
                            <div className="section-icon">
                              <i className="ri-bank-card-line"></i>
                            </div>

                            <div>
                              <h6 className="mb-1 fw-semibold">Bank Details</h6>

                              <small className="text-muted">
                                Enter the basic information of the bank account.
                              </small>
                            </div>
                          </div>

                          <div className="row g-3 mt-1">
                            {/* Account Number */}

                            <div className="col-xl-6 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="account_no"
                                  id="account_no"
                                  className="form-control text-uppercase"
                                  placeholder="Account Number"
                                  required
                                  autoComplete="off"
                                  onChange={(e) => {
                                    e.target.value =
                                      e.target.value.toUpperCase();
                                  }}
                                />

                                <label htmlFor="account_no">
                                  <i className="ri-bank-card-line me-1"></i>
                                  Account Number
                                  <span className="text-danger ms-1">*</span>
                                </label>
                              </div>
                            </div>

                            {/* IFSC */}

                            <div className="col-xl-6 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="ifsc_code"
                                  id="ifsc_code"
                                  className="form-control text-uppercase"
                                  placeholder="IFSC Code"
                                  maxLength={11}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    e.target.value =
                                      e.target.value.toUpperCase();
                                  }}
                                />

                                <label htmlFor="ifsc_code">
                                  <i className="ri-code-box-line me-1"></i>
                                  IFSC Code
                                </label>
                              </div>

                              <small className="text-muted ms-1">
                                Example: SBIN0001234
                              </small>
                            </div>

                            {/* Owner */}

                            <div className="col-xl-6 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="owner_name"
                                  id="owner_name"
                                  className="form-control text-uppercase"
                                  placeholder="Owner Name"
                                  autoComplete="off"
                                  onChange={(e) => {
                                    e.target.value =
                                      e.target.value.toUpperCase();
                                  }}
                                />

                                <label htmlFor="owner_name">
                                  <i className="ri-user-3-line me-1"></i>
                                  Account Holder Name
                                </label>
                              </div>
                            </div>

                            {/* Branch */}

                            <div className="col-xl-6 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="branch"
                                  id="branch"
                                  className="form-control text-uppercase"
                                  placeholder="Branch"
                                  autoComplete="off"
                                  onChange={(e) => {
                                    e.target.value =
                                      e.target.value.toUpperCase();
                                  }}
                                />

                                <label htmlFor="branch">
                                  <i className="ri-building-4-line me-1"></i>
                                  Branch
                                </label>
                              </div>
                            </div>

                            {/* Amount */}

                            <div className="col-xl-6 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  name="amount"
                                  id="amount"
                                  className="form-control"
                                  placeholder="Amount"
                                  step="any"
                                  min="0"
                                  autoComplete="off"
                                />

                                <label htmlFor="amount">
                                  <i className="ri-money-rupee-circle-line me-1"></i>
                                  Opening Balance
                                </label>
                              </div>

                              <small className="text-muted ms-1">
                                Enter the current opening balance, if
                                applicable.
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* =================================================
                            UPI SECTION
                        ================================================= */}

                        <div className="form-section mt-4">
                          <div className="form-section-header">
                            <div className="section-icon upi-icon">
                              <i className="ri-qr-code-line"></i>
                            </div>

                            <div>
                              <h6 className="mb-1 fw-semibold">
                                UPI & QR Payment
                              </h6>

                              <small className="text-muted">
                                Configure UPI details for receiving digital
                                payments.
                              </small>
                            </div>
                          </div>

                          <div className="row g-3 mt-1">
                            {/* QR Upload */}

                            <div className="col-lg-6">
                              <div className="qr-upload-card">
                                <div className="qr-upload-header">
                                  <div>
                                    <h6 className="mb-1">QR Code</h6>

                                    <small className="text-muted">
                                      Upload your UPI QR image
                                    </small>
                                  </div>

                                  <i className="ri-qr-scan-2-line qr-header-icon"></i>
                                </div>

                                <div className="qr-upload-area">
                                  {qrImage ? (
                                    <div className="qr-preview-wrapper">
                                      <img
                                        src={URL.createObjectURL(qrImage)}
                                        alt="UPI QR Preview"
                                        className="qr-preview"
                                      />

                                      <div className="qr-file-info">
                                        <span className="fw-medium">
                                          {qrImage.name}
                                        </span>

                                        <small className="text-muted">
                                          {(qrImage.size / 1024).toFixed(1)} KB
                                        </small>
                                      </div>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setQrImage(null)}
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  ) : (
                                    <label
                                      htmlFor="qr_img"
                                      className="qr-drop-zone"
                                    >
                                      <div className="qr-drop-icon">
                                        <i className="ri-upload-cloud-2-line"></i>
                                      </div>

                                      <div className="fw-semibold">
                                        Upload QR Code
                                      </div>

                                      <small className="text-muted">
                                        PNG, JPG or JPEG
                                      </small>
                                    </label>
                                  )}

                                  <input
                                    type="file"
                                    name="qr_img"
                                    id="qr_img"
                                    className="d-none"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={(e) =>
                                      setQrImage(e.target.files[0] || null)
                                    }
                                  />
                                </div>
                              </div>
                            </div>

                            {/* UPI ID */}

                            <div className="col-lg-6">
                              <div className="upi-input-card">
                                <div className="upi-input-header">
                                  <div className="upi-logo">
                                    <i className="ri-smartphone-line"></i>
                                  </div>

                                  <div>
                                    <h6 className="mb-1">
                                      UPI ID
                                      {qrImage && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </h6>

                                    <small className="text-muted">
                                      Enter the UPI ID linked to this QR code.
                                    </small>
                                  </div>
                                </div>

                                <div className="form-floating form-floating-outline mt-3">
                                  <input
                                    type="text"
                                    name="upi_id"
                                    id="upi_id"
                                    className="form-control"
                                    placeholder="example@upi"
                                    value={upiId}
                                    required={!!qrImage}
                                    onChange={(e) =>
                                      setUpiId(e.target.value.trim())
                                    }
                                  />

                                  <label htmlFor="upi_id">
                                    <i className="ri-at-line me-1"></i>
                                    UPI ID
                                    {qrImage && (
                                      <span className="text-danger ms-1">
                                        *
                                      </span>
                                    )}
                                  </label>
                                </div>

                                <div className="upi-help">
                                  <i className="ri-information-line"></i>

                                  <span>
                                    UPI ID is required when a QR code is
                                    uploaded.
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* =================================================
                          FOOTER
                      ================================================= */}

                      <div className="bank-form-footer">
                        <div className="footer-info">
                          <i className="ri-information-line"></i>

                          <span>
                            Fields marked with
                            <strong className="text-danger ms-1">*</strong> are
                            required.
                          </span>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            type="reset"
                            className="btn btn-outline-secondary px-4"
                            disabled={loading}
                            onClick={() => {
                              setQrImage(null);
                              setUpiId("");
                            }}
                          >
                            <i className="ri-refresh-line me-1"></i>
                            Reset
                          </button>

                          <button
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="ri-save-3-line me-1"></i>
                                Save Bank Account
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          PAGE STYLES
      ===================================================== */}

      <style>
        {`

          .bank-page-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            padding: 4px 2px;
            flex-wrap: wrap;
          }

          .bank-header-left {
            display: flex;
            align-items: center;
            gap: 14px;
          }

          .bank-header-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(115, 103, 240, .10);
            color: #7367f0;
            font-size: 25px;
            flex-shrink: 0;
          }

          .bank-header-badge {
            padding: 8px 13px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
            background: rgba(40, 199, 111, .10);
            color: #28c76f;
          }

          .form-section {
            border: 1px solid rgba(0, 0, 0, .07);
            border-radius: 12px;
            padding: 20px;
          }

          .form-section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
          }

          .section-icon {
            width: 38px;
            height: 38px;
            border-radius: 9px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(115, 103, 240, .10);
            color: #7367f0;
            font-size: 19px;
            flex-shrink: 0;
          }

          .section-icon.upi-icon {
            background: rgba(0, 207, 232, .10);
            color: #00cfe8;
          }

          .qr-upload-card,
          .upi-input-card {
            border: 1px solid rgba(0, 0, 0, .07);
            border-radius: 11px;
            padding: 16px;
            height: 100%;
          }

          .qr-upload-header,
          .upi-input-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
          }

          .qr-header-icon {
            font-size: 25px;
            color: #7367f0;
          }

          .qr-upload-area {
            margin-top: 14px;
          }

          .qr-drop-zone {
            min-height: 170px;
            border: 1.5px dashed rgba(115, 103, 240, .35);
            border-radius: 10px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all .2s ease;
          }

          .qr-drop-zone:hover {
            border-color: #7367f0;
            background: rgba(115, 103, 240, .035);
          }

          .qr-drop-icon {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(115, 103, 240, .10);
            color: #7367f0;
            font-size: 23px;
            margin-bottom: 9px;
          }

          .qr-preview-wrapper {
            min-height: 170px;
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 12px;
            border-radius: 10px;
            border: 1px solid rgba(0, 0, 0, .07);
          }

          .qr-preview {
            width: 125px;
            height: 125px;
            object-fit: contain;
            border-radius: 8px;
            border: 1px solid rgba(0, 0, 0, .08);
            padding: 5px;
          }

          .qr-file-info {
            display: flex;
            flex-direction: column;
            gap: 3px;
            flex: 1;
            min-width: 0;
          }

          .qr-file-info span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .upi-logo {
            width: 40px;
            height: 40px;
            border-radius: 9px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(40, 199, 111, .10);
            color: #28c76f;
            font-size: 20px;
          }

          .upi-help {
            display: flex;
            align-items: flex-start;
            gap: 7px;
            margin-top: 12px;
            padding: 9px 11px;
            border-radius: 7px;
            background: rgba(0, 207, 232, .06);
            color: #6f6b7d;
            font-size: 11px;
            line-height: 1.5;
          }

          .upi-help i {
            color: #00cfe8;
            font-size: 15px;
            margin-top: 1px;
          }

          .bank-form-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
            padding: 16px 24px;
            border-top: 1px solid rgba(0, 0, 0, .07);
          }

          .footer-info {
            display: flex;
            align-items: center;
            gap: 7px;
            color: #6f6b7d;
            font-size: 12px;
          }

          .footer-info i {
            font-size: 16px;
          }

          @media (max-width: 768px) {

            .bank-page-header {
              align-items: flex-start;
            }

            .bank-header-badge {
              display: none;
            }

            .form-section {
              padding: 15px;
            }

            .bank-form-footer {
              flex-direction: column;
              align-items: stretch;
            }

            .footer-info {
              justify-content: center;
            }

            .bank-form-footer .d-flex {
              width: 100%;
            }

            .bank-form-footer button {
              flex: 1;
            }

          }

          @media (max-width: 480px) {

            .bank-header-left {
              align-items: flex-start;
            }

            .bank-header-icon {
              width: 42px;
              height: 42px;
              font-size: 21px;
            }

            .qr-preview-wrapper {
              flex-wrap: wrap;
            }

            .qr-preview {
              width: 100px;
              height: 100px;
            }

          }

        `}
      </style>
    </>
  );
};

export default BanksAdd;
